// Whether a table cell's value is in its cited passage, and how the passage
// spells it (#12090, #12452). A derived or computed value has no source, so a
// cell keeps a document source only when this finds its value.
import {
  figureHits,
  figureRegex,
} from "../common/utils/addOnServices/figureMatch";
import {
  type Decimal,
  decimalOf,
  roundHalfUp,
  shift,
  significantDigits,
} from "./decimalText";

const collapseText = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\s+/g, " ")
    .trim();

// A fraction as a table cell stores it: sign, digits, a non-zero fraction.
// A whole number is a count, never a fraction of one: `1` is not `100%`.
const FRACTION = /^([-+]?)(\d+)\.(\d*[1-9]\d*)$/;

// A number as a table cell renders it, exponent form included: a very small
// value prints as `1.2e-7`.
const NUMBER = /^[-+]?\d+(?:\.\d+)?(?:e[-+]?\d+)?$/i;

// Excel's exponent notation as a spreadsheet passage keeps it: `-1.2E-05`.
const SCIENTIFIC = /[-+\u2212]?\d+(?:\.\d+)?[eE][-+]\d+/g;

/** The value ×100 as a decimal string, by moving the point rather than
 *  multiplying floats (`2.093 * 100` is `209.29999999999998`): `2.093` →
 *  `209.3`, `-0.04` → `-4`. Null for anything that is not a fraction. */
const asPercent = (value: string): string | null => {
  const parts = value.match(FRACTION);
  if (!parts) return null;
  const [, sign, integer, fraction] = parts;
  const padded = fraction.padEnd(2, "0");
  const whole = `${integer}${padded.slice(0, 2)}`.replace(/^0+(?=\d)/, "");
  const rest = padded.slice(2).replace(/0+$/, "");
  return `${sign === "-" ? "-" : ""}${whole}${rest ? `.${rest}` : ""}`;
};

/** The passage's percent spelling of a fraction (`209.3%` for `2.093`,
 *  `(4.0%)` for `-0.04`): the figure matcher's hit for the value ×100, only
 *  where the page prints the `%`, so `0.5` never finds a bare `50`. */
const percentSpelling = (value: string, passage: string): string | null => {
  const percent = asPercent(value);
  if (percent === null) return null;
  const hit = figureHits(figureRegex(percent), passage).find((h) =>
    /%\s*\)?$/.test(h.text.trimEnd())
  );
  return hit ? hit.text.trim() : null;
};

/** The passage's scientific spelling of the same number (`-1.2E-05` for
 *  `-0.000012`). Both strings name one decimal, so `Number` reads both to
 *  the same double. */
const scientificSpelling = (value: string, passage: string): string | null => {
  if (!NUMBER.test(value)) return null;
  const wanted = Number(value);
  const hit = passage
    .match(SCIENTIFIC)
    ?.find((token) => Number(token.replace("\u2212", "-")) === wanted);
  return hit ?? null;
};

// A figure as a page prints it, for the unit-conversion pass: an optional
// sign or opening accounting paren, an optional currency, the digits with
// thousands commas, a fraction or an exponent, an optional magnitude word,
// the closing paren of an accounting negative, and a percent sign when there
// is one (which rules the figure out).
const PAGE_FIGURE =
  /(?<![\d.,])(\(|[-\u2212])?\s?([$\u20ac\u00a3\u00a5]\s?)?(\d[\d,]*(?:\.\d+)?(?:[eE][-+]?\d+)?)(\s?(?:bn|mm|[mkb])(?![a-z]))?(\s?\))?(\s?%)?/gi;

// A bare four-digit integer in this range is a year on nearly every fund
// page, and a multiple like 2.02x sits beside a vintage often enough that
// reading the year as the multiple a thousandfold away is not rare.
const YEAR = /^(19|20)\d\d$/;

// The scales a change of units moves a figure by: thousands, millions,
// billions, either way.
const UNIT_SHIFTS: number[] = [3, 6, 9];

/** Fewer digits than this and a conversion match is a coincidence waiting
 *  to happen: `5` is on every page as `5,000`. */
const MIN_CONVERSION_DIGITS = 3;

interface Magnitude {
  /** The significant digits, leading and trailing zeros dropped. */
  digits: string;
  /** The power of ten of the first significant digit. */
  exponent: number;
}

/** A number's significant digits and scale, read off its text so `23500`
 *  and `2.35E+10` compare exactly: ("235", 4) and ("235", 10). */
const magnitudeOf = (text: string): Magnitude | null => {
  const parts = text
    .replace(/,/g, "")
    .match(/^(\d*)(?:\.(\d*))?(?:e([-+]?\d+))?$/i);
  if (!parts) return null;
  const [, integer, fraction = "", power = "0"] = parts;
  const all = `${integer}${fraction}`;
  const leadingZeros = all.length - all.replace(/^0+/, "").length;
  const digits = all.slice(leadingZeros).replace(/0+$/, "");
  if (digits === "") return null;
  return {
    digits,
    exponent: integer.length - 1 - leadingZeros + Number(power),
  };
};

/** The passage's figure for a value it prints in other units (`54.7 bn` for
 *  `54700` in $MM, `2.35E+10` for `23500`): the same significant digits and
 *  sign, a thousand, a million or a billion apart. A conversion of units is
 *  the page's own figure, not a computation (Gerry, 2026-09-30, #12452). A
 *  differently rounded one (`54775` against `54.7 bn`) has other digits and
 *  is not held. The price: a computed value whose digits happen to equal an
 *  unrelated figure a thousandfold away keeps a source. */
const convertedSpelling = (value: string, passage: string): string | null => {
  if (!NUMBER.test(value)) return null;
  const negative = value.startsWith("-");
  const wanted = magnitudeOf(value.replace(/^[-+]/, ""));
  if (!wanted || wanted.digits.length < MIN_CONVERSION_DIGITS) return null;
  for (const match of passage.matchAll(PAGE_FIGURE)) {
    const [, sign, currency = "", digits, unit = "", close, percent] = match;
    if (percent) continue;
    // A comma after the figure is the sentence's, not a thousands group.
    const number = digits.replace(/,+$/, "");
    // An opening paren is a sign only with its closing paren: "(1,250)" is
    // a negative, "(1,250 employees)" a parenthetical.
    const bracketed = sign === "(" && Boolean(close);
    if (sign === "(" && !bracketed && negative) continue;
    const signed = bracketed || (sign !== undefined && sign !== "(");
    if (signed !== negative) continue;
    if (!currency && !unit && YEAR.test(number)) continue;
    const found = magnitudeOf(number);
    if (!found || found.digits !== wanted.digits) continue;
    if (!UNIT_SHIFTS.includes(Math.abs(found.exponent - wanted.exponent))) {
      continue;
    }
    const core = `${currency}${number}${unit}`.trim();
    if (bracketed) return `(${core})`;
    return `${sign && sign !== "(" ? sign : ""}${core}`;
  }
  return null;
};

// A plain decimal as a page prints it, for the rounding passes (#13498): an
// optional sign or opening accounting paren, an optional currency, the digits
// with thousands commas, a fraction, and the closing paren of an accounting
// negative. Never one with a scale word or a unit after it (`1.23M`,
// `55bps`, `0.55%`): the conversion and percent rules own those. A fraction
// is required, so a bare year is never one.
const PLAIN_DECIMAL =
  /(?<![\d.,])(\(|[-−])?\s?([$€£¥]\s?)?(\d[\d,]*\.\d+)(?![\d.])(?!\s?(?:%|bps|bn|mm|mn|thousand|million|billion|[mkbx])(?![a-z]))(\s?\))?/gi;

/** Fewer significant digits than this on the cell and a rounding match is a
 *  coincidence: `12` is every figure from 11.5 to 12.5. The same floor as
 *  the conversion rule. */
const MIN_ROUNDED_DIGITS = 3;

/** Fewer significant digits than this on the page and a fraction is a round
 *  figure (`0.5`, `0.125`), not a stored float: without a `%` on the cell,
 *  `125` is not a percent of it. */
const MIN_BARE_PERCENT_PAGE_DIGITS = 4;

interface PageDecimal {
  negative: boolean;
  value: Decimal;
  /** The figure as the page prints it, for the viewer to find. */
  spelling: string;
}

/** Every plain decimal the passage prints, with its sign. */
const plainDecimals = (passage: string): PageDecimal[] =>
  Array.from(passage.matchAll(PLAIN_DECIMAL), (match) => {
    const [, sign, currency = "", digits, close] = match;
    // An opening paren is a sign only with its closing paren: "(1.5 years)"
    // is a parenthetical.
    const bracketed = sign === "(" && Boolean(close);
    // The pattern requires the point: digits, then a fraction.
    const [int, frac = ""] = digits.replace(/,/g, "").split(".");
    const core = `${currency}${digits}`.trim();
    const minus = sign !== undefined && sign !== "(" ? sign : "";
    return {
      negative: bracketed || minus !== "",
      value: { int, frac },
      spelling: bracketed ? `(${core})` : `${minus}${core}`,
    };
  });

/** A cell's number, signed, from its stored text (`-1235`, `20.00%`). */
interface CellNumber {
  negative: boolean;
  value: Decimal;
  percent: boolean;
}

const CELL_PERCENT = /^([-+]?)(\d+(?:\.\d+)?)\s?%$/;

const cellNumberOf = (text: string): CellNumber | null => {
  const percent = text.match(CELL_PERCENT);
  const sign = percent ? percent[1] : text.startsWith("-") ? "-" : "";
  const digits = percent ? percent[2] : text.replace(/^[-+]/, "");
  if (!percent && !NUMBER.test(text)) return null;
  const value = decimalOf(digits);
  if (!value || significantDigits(value) < MIN_ROUNDED_DIGITS) return null;
  return { negative: sign === "-", value, percent: Boolean(percent) };
};

/** The passage's figure that rounds to the cell (`143085926.57999998` for
 *  `143085927`): rounded half-up to the cell's decimals it is the cell. A
 *  page less precise than the cell only gains zeros, so it rounds to the
 *  cell only when it states it, which the exact match already took. */
const roundedSpelling = (cell: CellNumber, passage: string): string | null => {
  const places = cell.value.frac.length;
  const wanted = roundHalfUp(cell.value, places);
  const hit = plainDecimals(passage).find(
    (page) =>
      page.negative === cell.negative &&
      roundHalfUp(page.value, places) === wanted
  );
  return hit ? hit.spelling : null;
};

/** The passage's fraction whose percent rounds to the cell (`0.2` for
 *  `20.00%`, `0.3085972895804296` for `30.86`): ×100 by moving the point,
 *  then rounded half-up to the cell's decimals. A cell without a `%` takes
 *  only a fraction of four significant digits or more, a stored float. */
const fractionSpelling = (cell: CellNumber, passage: string): string | null => {
  const places = cell.value.frac.length;
  const wanted = roundHalfUp(cell.value, places);
  const hit = plainDecimals(passage).find((page) => {
    if (page.negative !== cell.negative) return false;
    const stored = {
      int: page.value.int,
      frac: page.value.frac.replace(/0+$/, ""),
    };
    if (stored.frac === "") return false;
    if (
      !cell.percent &&
      significantDigits(stored) < MIN_BARE_PERCENT_PAGE_DIGITS
    )
      return false;
    return roundHalfUp(shift(stored, 2), places) === wanted;
  });
  return hit ? hit.spelling : null;
};

/** The rounding passes (#13498), after every exact and conversion rule. */
const roundingSpelling = (value: string, passage: string): string | null => {
  const cell = cellNumberOf(value);
  if (!cell) return null;
  return (
    (cell.percent ? null : roundedSpelling(cell, passage)) ??
    fractionSpelling(cell, passage)
  );
};

/** The passage's spelling of a table cell's value, or null when the passage
 *  does not hold it: a figure in any spelling the viewer's figure matcher
 *  highlights (`12082` in `$12,082M`, `10` in `8-10%`), a fraction the page
 *  prints as a percent (`209.3%` for `2.093`), a number it prints in
 *  exponent notation (`-1.2E-05`) or in other units (`54.7 bn` for `54700`),
 *  a rounding of a figure it prints more precisely (`143085926.57999998`
 *  for `143085927`), a percent of a fraction it prints (`0.2` for
 *  `20.00%`), other text case-insensitively with whitespace collapsed. A
 *  computed or reworded value is not in its passage. */
export const passageSpelling = (
  value: string,
  passage: string
): string | null => {
  const figure: RegExp | null = figureRegex(value);
  if (figure?.test(passage)) return value;
  if (NUMBER.test(value)) {
    return (
      percentSpelling(value, passage) ??
      scientificSpelling(value, passage) ??
      convertedSpelling(value, passage) ??
      roundingSpelling(value, passage)
    );
  }
  if (figure) return roundingSpelling(value, passage);
  const wanted = collapseText(value);
  return wanted !== "" && collapseText(passage).includes(wanted) ? value : null;
};
